/** Map react-admin form record ↔ .NET UpsertPraInvoiceRequest. */

export type PraInvoiceLineForm = {
    id?: string;
    productProfileId?: string;
    productNo?: string;
    productName?: string;
    quantity?: number;
    unitPrice?: number;
    taxRate?: number;
    fbrSalesTaxRateId?: number | null;
    /** Multiple shared tax-rate rows (additive); chips in UI — same fbrSalesTaxRates master as FBR. */
    fbrSalesTaxRateIds?: number[];
    /** Discount percent (0-100) */
    discountRate?: number;
    furtherTax?: number;
    pctCode?: string;
    itemCode?: string;
    refUsin?: string;
    remarks?: string;
};

function toDateOnlyLocal(value: unknown): string | undefined {
    if (value == null || value === '') return undefined;
    if (typeof value === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return value.trim();
        if (value.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
        return undefined;
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, '0');
        const d = String(value.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }
    return undefined;
}

function lineHasProduct(l: PraInvoiceLineForm | undefined): boolean {
    const id = l?.productProfileId;
    if (id == null || id === '') return false;
    return String(id).replace(/-/g, '').length > 0;
}

/** Normalize API record so nested lines register correctly in react-hook-form (Guids, decimals). */
export function normalizePraInvoiceRecord(data: Record<string, unknown> | null | undefined) {
    if (!data || typeof data !== 'object') return data;
    const raw = (data.lines as PraInvoiceLineForm[] | undefined) ?? [];
    const lines = raw.map(l => {
        const apiIds = (l as any).fbrSalesTaxRateIds as unknown;
        let fbrSalesTaxRateIds: number[] = [];
        if (Array.isArray(apiIds) && apiIds.length > 0) {
            fbrSalesTaxRateIds = apiIds.map(x => Number(x)).filter(x => Number.isFinite(x) && x > 0);
        } else if ((l as any).fbrSalesTaxRateId != null && (l as any).fbrSalesTaxRateId !== '') {
            const one = Number((l as any).fbrSalesTaxRateId);
            if (Number.isFinite(one) && one > 0) fbrSalesTaxRateIds = [one];
        }
        return {
            id: l.id != null ? String(l.id) : undefined,
            productProfileId:
                l.productProfileId != null && l.productProfileId !== ''
                    ? String(l.productProfileId)
                    : '',
            productNo: l.productNo,
            productName: l.productName,
            quantity: Number(l.quantity) || 0,
            unitPrice: Number(l.unitPrice) || 0,
            taxRate: Number(l.taxRate) || 0,
            fbrSalesTaxRateId:
                (l as any).fbrSalesTaxRateId == null || (l as any).fbrSalesTaxRateId === ''
                    ? null
                    : Number((l as any).fbrSalesTaxRateId),
            fbrSalesTaxRateIds,
            discountRate: Number((l as any).discountRate) || 0,
            furtherTax: Number((l as any).furtherTax) || 0,
            pctCode: l.pctCode ?? '',
            itemCode: l.itemCode ?? '',
            refUsin: l.refUsin ?? '',
            remarks: l.remarks ?? '',
        };
    });
    const topId = (data as any).id ?? (data as any).Id;
    return {
        ...data,
        ...(topId != null && topId !== '' ? { id: String(topId) } : {}),
        lines,
        usin: (data as any).usin != null ? String((data as any).usin) : '',
        refUsin: (data as any).refUsin ?? '',
        reference:
            (data as any).reference == null || (data as any).reference === ''
                ? ''
                : String((data as any).reference),
        isLocked: Boolean((data as any).isLocked),
        praInvoiceNumber: (data as any).praInvoiceNumber ?? undefined,
        postedAtUtc: (data as any).postedAtUtc,
        praLastError: (data as any).praLastError ?? null,
        praLastResponseJson: (data as any).praLastResponseJson ?? null,
        paymentMode: Number((data as any).paymentMode) || 1,
        invoiceType: Number((data as any).invoiceType) || 1,
    };
}

export function mapPraInvoiceToUpsertBody(data: Record<string, unknown>) {
    const linesRaw = (data.lines as PraInvoiceLineForm[] | undefined) ?? [];
    const lines = linesRaw
        .filter(lineHasProduct)
        .map(l => {
            const fromArr =
                Array.isArray(l.fbrSalesTaxRateIds) && l.fbrSalesTaxRateIds.length > 0
                    ? l.fbrSalesTaxRateIds.map(x => Number(x)).filter(x => Number.isFinite(x) && x > 0)
                    : l.fbrSalesTaxRateId != null && Number(l.fbrSalesTaxRateId) > 0
                      ? [Number(l.fbrSalesTaxRateId)]
                      : [];
            return {
                productProfileId: String(l.productProfileId),
                quantity: Number(l.quantity) || 0,
                unitPrice: Number(l.unitPrice) || 0,
                taxRate: Number(l.taxRate) || 0,
                fbrSalesTaxRateId:
                    fromArr.length > 0 ? fromArr[0] : l.fbrSalesTaxRateId == null ? null : Number(l.fbrSalesTaxRateId),
                fbrSalesTaxRateIds: fromArr.length > 0 ? fromArr : undefined,
                discountRate: Number((l as any).discountRate) || 0,
                furtherTax: Number((l as any).furtherTax) || 0,
                pctCode: String(l.pctCode ?? ''),
                itemCode: String(l.itemCode ?? ''),
                refUsin: l.refUsin ? String(l.refUsin) : undefined,
                remarks: String(l.remarks ?? ''),
            };
        });

    return {
        reference: data.reference as string | undefined,
        // Send the manually-typed USIN (form field is `usin`). When blank the server
        // auto-assigns the next sequence number, so we omit it rather than sending "".
        usin: (data as any).usin ? String((data as any).usin).trim() : undefined,
        refUsin: (data as any).refUsin ? String((data as any).refUsin).trim() : undefined,
        customerPartyId: Number(data.customerPartyId) || 0,
        // Date-only to avoid UTC shifting; backend persists DATE and uses it for the PRA payload.
        invoiceDate: toDateOnlyLocal((data as any).invoiceDate),
        paymentMode: Number((data as any).paymentMode) || 1,
        invoiceType: Number((data as any).invoiceType) || 1,
        lines,
    };
}
